<script lang="ts">
  import { onMount } from "svelte"
  import { flip } from "svelte/animate"
  import Sortable from "sortablejs"
  import {
    toast,
    xhr,
    drag_enabled,
    sortElementsForSortable,
    wait
  } from "$app/utils"
  import { sortBy } from "lodash"
  import EditGoalStatus from "../Goals/EditGoalStatus.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { MANAGEMENT_ROLES } from "$app/members"
  import { appStore } from "$app/store.svelte"

  interface Props {
    department: DepartmentType
    goals: GoalType[]
    quarterlyGoalCount: number
    title: string
    hideCompleted: boolean
    editable: boolean
    showAddButton?: boolean
    onopenNewGoalModal: () => void
    onopenModal: (goal: GoalType) => void
    onupdateGoals: (goals: GoalType[]) => void
  }

  let props = $props()
  let {
    department,
    quarterlyGoalCount = 0,
    title,
    hideCompleted,
    editable = false,
    onopenNewGoalModal = () => {},
    onopenModal,
    onupdateGoals,
    showAddButton = true
  }: Props = props
  let goals = $state(
    sortBy(
      props.goals.filter((g) => g.assignee_id === department.id),
      "assignee_position"
    )
  )

  let dnd_enabled = editable
  let showPlaceholder = $state(true)

  let innerWidth = $state(0)

  onMount(() => {
    dnd_enabled = drag_enabled(innerWidth, editable)
    setSortable()
  })

  const flipDurationMs = 300

  function showGoalLinkage(goal: GoalType) {
    const annualGoal = document.querySelector(
      `[data-goal-id='${goal.parent_goal_id}']`
    )
    if (annualGoal) {
      annualGoal.classList.add("bg-indigo-50")

      const ltGoalId = annualGoal.getAttribute("data-parent-goal-id")
      const ltGoal = document.querySelector(`[data-goal-id='${ltGoalId}']`)
      if (ltGoal) {
        ltGoal.classList.add("bg-indigo-50")
      }
    }
  }
  function hideGoalLinkage(goal: GoalType) {
    const annualGoal = document.querySelector(
      `[data-goal-id='${goal.parent_goal_id}']`
    )
    if (annualGoal) {
      annualGoal.classList.remove("bg-indigo-50")

      const ltGoalId = annualGoal.getAttribute("data-parent-goal-id")
      const ltGoal = document.querySelector(`[data-goal-id='${ltGoalId}']`)
      if (ltGoal) {
        ltGoal.classList.remove("bg-indigo-50")
      }
    }
  }

  let sortableSection: HTMLElement

  function setSortable(): void {
    if (!sortableSection) return
    const sort = Sortable.create(sortableSection, {
      group: "QuarterlyGoals",
      animation: 200,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      dragClass: "border-green-400",
      onEnd: async (e: EventSortType) => {
        sort.options.sort = false
        await updateSort(e)
        sort.options.sort = true
      }
    })
  }

  async function updateSort(e: EventSortType) {
    const sameContainer =
      e.from.dataset.department_id === e.to.dataset.department_id

    if (e.newIndex === e.oldIndex && sameContainer) return

    const deptId = parseInt(e.to.dataset.department_id)
    let newGoals: GoalType[] = []

    if (sameContainer) {
      newGoals = sortElementsForSortable(
        goals,
        e.newIndex,
        e.oldIndex
      ) as GoalType[]
    } else {
      let tempGoals = appStore.quarterly_goals
        .filter((g) => g.assignee_id === deptId)
        .sort((a, b) => a.assignee_position - b.assignee_position)

      newGoals = [
        ...tempGoals.slice(0, e.newIndex),
        goals[e.oldIndex],
        ...tempGoals.slice(e.newIndex, tempGoals.length + 1)
      ]
    }

    let resp: any = await saveSortOrder(newGoals, deptId)

    if (resp) {
      onupdateGoals(resp)
    }
    showPlaceholder = true
  }

  async function saveSortOrder(
    newGoals: GoalType[],
    department_id: number
  ): Promise<any> {
    try {
      const resp = await xhr.post(
        `/plans/${appStore.plan.id}/goals/sort_by_department.json`,
        { id: department_id, goals: newGoals.map((s) => s.id) }
      )
      return resp.data
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating the sort order", "error-")

      const tmpGoals = [...appStore.quarterly_goals]
      appStore.quarterly_goals = []
      await wait(10)
      appStore.quarterly_goals = tmpGoals
      return false
    }
  }
</script>

<svelte:window bind:innerWidth />

<div class="bg-white shadow border-l-4 border-quarterly-goal">
  <div class="bg-white px-4 py-4 sm:px-6 relative">
    <div class="flex items-center justify-start md:justify-center">
      <h3 class="leading-6 font-semibold text-black text-center">
        {title}
      </h3>
    </div>
  </div>

  {#if goals.length === 0 && showPlaceholder}
    <div class="flex justify-center items-center w-full py-8">
      <div
        class="inline-flex items-center px-6 py-2 text-base font-semibold text-gray-800"
      >
        {#if hideCompleted}
          No Uncompleted Goals Found
        {:else}
          No Goals Found
        {/if}
      </div>
      {#if showAddButton && MANAGEMENT_ROLES.includes(appStore.current_user.role)}
        <button
          class="rounded bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-600 shadow-sm hover:bg-indigo-100"
          onclick={onopenNewGoalModal}
        >
          Add Goal
        </button>
      {/if}
    </div>
  {/if}
  <div class="flex flex-col border-t-1 border-gray-200 relative">
    {#if goals.length === 0 && quarterlyGoalCount > 0 && showPlaceholder}
      <div class="absolute top-2 left-4 w-full text-center text-gray-600">
        Drag existing quarterly goal to assign them to this department
      </div>
    {/if}
    <ul
      class="divide-y divide-gray-200"
      class:bg-gray-100={goals.length === 0}
      bind:this={sortableSection}
      data-department_id={department.id}
      style="min-height:40px"
      ondrop={() => (showPlaceholder = goals.length !== 0)}
    >
      {#each goals as goal, idx (goal.id)}
        <li
          animate:flip={{ duration: flipDurationMs }}
          class="block hover:bg-indigo-100 {editable
            ? 'hover:cursor-move'
            : ''}"
          data-goal-id={goal.id}
          data-parent-goal-id={goal.parent_goal_id}
          class:hidden={hideCompleted &&
            (goal.status === "complete" || goal.status === "not_met")}
          onfocus={() => showGoalLinkage(goal)}
          onmouseover={() => showGoalLinkage(goal)}
          onblur={() => hideGoalLinkage(goal)}
          onmouseout={() => hideGoalLinkage(goal)}
          ondblclick={() => onopenModal(goal)}
        >
          <div class="pl-2 pr-0 py-1 sm:pl-4">
            <div class="flex items-center justify-between select-none">
              <p
                class="text-sm font-medium text-black hover:text-indigo-600 truncate select-none"
              >
                <span class="text-gray-400 text-xs">{idx + 1}.</span>
                {goal.name}
              </p>

              <div class="mx-1 flex-shrink-0 flex items-center justify-end">
                <EditGoalStatus
                  goalOrTask={goal}
                  type="Goal"
                  show_border={false}
                  small={true}
                  {editable}
                  onupdate={(data) => (goal.status = data.status)}
                />
              </div>
            </div>
          </div>
        </li>
      {/each}
    </ul>
  </div>
</div>
